import React, { useState } from 'react'
import {
  Box,
  Button,
  Card,
  TextField,
  MenuItem,
  Typography,
  Avatar,
  IconButton,
  Chip,
  Tooltip,
  Grid,
  InputAdornment,
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Stack,
  CircularProgress,
  TablePagination,
  Checkbox,
  CardContent,
  CardActions,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as VisibilityIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Search as SearchIcon,
  FilterList as FilterListIcon,
  LocalOffer,
  Inventory,
  CheckCircle,
  Cancel,
  Warning,
  Category,
  ShoppingCart,
  Refresh,
  Clear,
  Inventory2 as Package,
  Scale,
  Straighten,
  ViewList,
  ViewModule,
} from '@mui/icons-material'
import { useSnackbar } from 'notistack'
import { useNavigate } from 'react-router-dom'
import { usePermissions } from '../hooks/usePermissions'
import {
  useGetProductsQuery,
  useDeleteProductMutation,
} from '../store/services/productApi'
import { useGetCategoriesQuery } from '../store/services/categoryApi'
import { Product, ProductStatus } from '../types/product'
import { DataTable, TableColumn, TableAction } from '../components/ui'
import BulkActionBar, { getProductBulkActions } from '../components/ui/BulkActionBar'
import ProgressManager from '../components/ui/ProgressManager'
import useBulkOperations from '../hooks/useBulkOperations'
import ProductImportDialog from '../components/products/ProductImportDialog'
import ProductExportDialog from '../components/products/ProductExportDialog'
import { getAbsoluteImageUrl } from '../utils/imageUtils'

const Products: React.FC = () => {
  const navigate = useNavigate()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<ProductStatus | ''>('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [priceMin, setPriceMin] = useState('')
  const [priceMax, setPriceMax] = useState('')
  const [stockMin, setStockMin] = useState('')
  const [stockMax, setStockMax] = useState('')
  const [debouncedPriceMin, setDebouncedPriceMin] = useState('')
  const [debouncedPriceMax, setDebouncedPriceMax] = useState('')
  const [debouncedStockMin, setDebouncedStockMin] = useState('')
  const [debouncedStockMax, setDebouncedStockMax] = useState('')
  const [selectedRows, setSelectedRows] = useState<string[]>([])
  const [importDialogOpen, setImportDialogOpen] = useState(false)
  const [exportDialogOpen, setExportDialogOpen] = useState(false)
  const [viewMode, setViewMode] = useState<'table' | 'card'>('table')

  // Bulk operations
  const {
    operations,
    executeBulkOperation,
    clearCompletedOperations,
    clearAllOperations
  } = useBulkOperations()
  const { enqueueSnackbar } = useSnackbar()
  const { hasPermission } = usePermissions()
  const theme = useTheme()
  const isBelowMd = useMediaQuery(theme.breakpoints.down('md'))
  const isBelowLg = useMediaQuery(theme.breakpoints.down('lg'))

  // Debounce search term
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm)
      setPage(1) // Reset to first page when search changes
    }, 500)
    return () => clearTimeout(timer)
  }, [searchTerm])

  // Debounce price/stock range filters
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedPriceMin(priceMin)
      setDebouncedPriceMax(priceMax)
      setDebouncedStockMin(stockMin)
      setDebouncedStockMax(stockMax)
      setPage(1)
    }, 500)
    return () => clearTimeout(timer)
  }, [priceMin, priceMax, stockMin, stockMax])

  // Reset page when filters change
  React.useEffect(() => {
    setPage(1)
  }, [statusFilter, categoryFilter])

  // Fetch products from API
  const { data, isLoading, isFetching, refetch, error } = useGetProductsQuery({
    page,
    per_page: pageSize,
    search: debouncedSearch || undefined,
    status: statusFilter || undefined,
    category_id: categoryFilter || undefined,
    price_min: debouncedPriceMin ? Number(debouncedPriceMin) : undefined,
    price_max: debouncedPriceMax ? Number(debouncedPriceMax) : undefined,
    stock_min: debouncedStockMin ? Number(debouncedStockMin) : undefined,
    stock_max: debouncedStockMax ? Number(debouncedStockMax) : undefined,
  })

  // Fetch categories for filter dropdown
  const { data: categoriesData, isLoading: isLoadingCategories } = useGetCategoriesQuery({ per_page: 100 })

  // Show user-facing error message when products fail to load
  React.useEffect(() => {
    if (error) {
      const err = error as any

      // Show user-friendly error message
      let errorMessage = 'Failed to load products.'
      if (err?.status === 401) {
        errorMessage = 'Please log in to view products.'
      } else if (err?.status === 404) {
        errorMessage = 'Products endpoint not found. Please check API configuration.'
      } else if (err?.status >= 500) {
        errorMessage = 'Server error. Please try again later.'
      }

      enqueueSnackbar(errorMessage, { variant: 'error' })
    }
  }, [data, error, enqueueSnackbar])

  // Get filtered products from API response
  const filteredProducts = React.useMemo(() => {
    return data?.items || []
  }, [data])

  const [deleteProduct] = useDeleteProductMutation()

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this product?')) return

    try {
      await deleteProduct(id).unwrap()
      enqueueSnackbar('Product deleted successfully', { variant: 'success' })
    } catch (error) {
      enqueueSnackbar('Failed to delete product', { variant: 'error' })
    }
  }

  const handleView = (product: Product) => {
    navigate(`/admin/products/${product.id}/view`)
  }

  const handleEdit = (product: Product) => {
    navigate(`/admin/products/${product.id}/edit`)
  }

  const handleCreate = () => {
    navigate('/admin/products/create')
  }

  // Bulk Actions
  const handleBulkDelete = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Delete Products',
        description: `Deleting ${selectedIds.length} products`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        let successful = 0
        let failed = 0
        const errors: string[] = []

        for (let i = 0; i < items.length; i++) {
          try {
            await deleteProduct(items[i]).unwrap()
            successful++
            onProgress(i + 1, `Deleted product ${i + 1} of ${items.length}`)
          } catch (error: any) {
            failed++
            errors.push(error?.data?.detail || error?.message || `Failed to delete product ${items[i]}`)
            onProgress(i + 1, `Failed to delete product ${i + 1} of ${items.length}`)
          }
        }

        return { successful, failed, warnings: 0, errors }
      }
    )
    setSelectedRows([])
  }

  const csvEscape = (value: unknown): string => {
    const str = value === null || value === undefined ? '' : String(value)
    return /[",\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str
  }

  const handleBulkExport = async (selectedIds: string[]) => {
    await executeBulkOperation(
      selectedIds,
      {
        title: 'Export Products',
        description: `Exporting ${selectedIds.length} products to CSV`,
        totalItems: selectedIds.length,
      },
      async (items, onProgress) => {
        const selectedProducts = filteredProducts.filter(p => items.includes(p.id))
        const header = [
          'Name', 'SKU', 'Item Code', 'HSN Code', 'Category', 'Brand', 'Status',
          'MRP', 'Sale Price', 'Wholesale Price', 'Purchase Price', 'GST Rate (%)',
          'MOQ', 'Inner Box Qty', 'Carton Qty', 'Weight (kg)', 'Is Active',
        ]
        const lines = [header.join(',')]

        for (let i = 0; i < items.length; i++) {
          const product = selectedProducts.find(p => p.id === items[i])
          if (product) {
            lines.push([
              csvEscape(product.name),
              csvEscape(product.sku),
              csvEscape(product.item_code),
              csvEscape(product.hsn_code),
              csvEscape((product as any).category?.name || ''),
              csvEscape((product as any).brand?.name || ''),
              csvEscape(product.status),
              csvEscape(product.mrp),
              csvEscape(product.sale_price),
              csvEscape(product.wholesale_price),
              csvEscape(product.purchase_price),
              csvEscape(product.gst_rate),
              csvEscape(product.moq),
              csvEscape(product.inner_box_quantity),
              csvEscape(product.carton_quantity),
              csvEscape(product.weight),
              csvEscape(product.is_active ? 'Yes' : 'No'),
            ].join(','))
          }
          onProgress(i + 1, `Processed product ${i + 1} of ${items.length}`)
        }

        const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `selected_products_${new Date().toISOString().slice(0, 10)}.csv`
        a.click()
        URL.revokeObjectURL(url)

        return { successful: selectedProducts.length, failed: items.length - selectedProducts.length, warnings: 0, errors: [] }
      }
    )
    setSelectedRows([])
  }

  const bulkActions = getProductBulkActions(
    handleBulkDelete,
    handleBulkExport
  ).filter((action) => {
    if (action.id === 'delete') return hasPermission('products.delete')
    if (action.id === 'export') return hasPermission('products.bulk_export')
    return true
  })

  const getStatusConfig = (status: ProductStatus) => {
    switch (status) {
      case ProductStatus.ACTIVE:
        return { color: 'success' as const, icon: <CheckCircle sx={{ fontSize: 14 }} />, label: 'Active' }
      case ProductStatus.ARCHIVED:
        return { color: 'error' as const, icon: <Cancel sx={{ fontSize: 14 }} />, label: 'Archived' }
      case ProductStatus.DRAFT:
        return { color: 'warning' as const, icon: <EditIcon sx={{ fontSize: 14 }} />, label: 'Draft' }
      case ProductStatus.DISCONTINUED:
        return { color: 'default' as const, icon: <Cancel sx={{ fontSize: 14 }} />, label: 'Discontinued' }
      case ProductStatus.OUT_OF_STOCK:
        return { color: 'warning' as const, icon: <Warning sx={{ fontSize: 14 }} />, label: 'Out of Stock' }
      default:
        return { color: 'default' as const, icon: null, label: status }
    }
  }

  // Enhanced columns with all B2B information
  const columns: TableColumn[] = [
    {
      id: 'product',
      label: 'Product Information',
      width: 300,
      render: (_, row: Product) => {
        const imageUrl = getAbsoluteImageUrl(row.image) || (row.images && row.images.length > 0 ? getAbsoluteImageUrl(row.images[0]) : null)
        const hasImage = !!imageUrl

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {hasImage ? (
              <Box
                component="img"
                src={imageUrl}
                alt={row.name}
                onError={(e: any) => {
                  // Hide image on error and show fallback
                  e.target.style.display = 'none'
                  if (e.target.nextSibling) {
                    e.target.nextSibling.style.display = 'flex'
                  }
                }}
                sx={{
                  width: 50,
                  height: 50,
                  objectFit: 'cover',
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                  bgcolor: 'grey.100'
                }}
              />
            ) : null}
            <Avatar
              variant="rounded"
              sx={{
                width: 50,
                height: 50,
                bgcolor: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
                fontSize: '1.2rem',
                fontWeight: 600,
                display: hasImage ? 'none' : 'flex'
              }}
            >
              {row.name.charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" fontWeight={600} noWrap sx={{ mb: 0.5 }}>
                {row.name}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label={row.sku}
                  size="small"
                  variant="outlined"
                  sx={{
                    fontSize: '0.7rem',
                    height: 18,
                    fontFamily: 'monospace',
                    borderColor: 'primary.main',
                    color: 'primary.main'
                  }}
                />
                <Typography variant="caption" color="text.secondary">
                  HSN: {row.hsn_code}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Brand: {(row as any).brand?.name || (row.product_metadata?.brand as string) || 'No Brand'}
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {row.is_clearance && (
                  <Chip
                    label="Clearance"
                    size="small"
                    color="warning"
                    sx={{ fontSize: '0.6rem', height: 16, fontWeight: 700 }}
                  />
                )}
                {row.is_deal && (
                  <Chip
                    label="Deal"
                    size="small"
                    color="error"
                    sx={{ fontSize: '0.6rem', height: 16, fontWeight: 700 }}
                  />
                )}
              </Box>
            </Box>
          </Box>
        )
      },
    },
    {
      id: 'category',
      label: 'Category',
      width: 180,
      render: (_, row: Product) => (
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Category sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" fontWeight={500}>
              {row.category?.name || 'Uncategorized'}
            </Typography>
          </Box>
        </Box>
      ),
    },
    {
      id: 'pricing',
      label: 'B2B Pricing Structure',
      width: 200,
      render: (_, row: Product) => {
        const mrp = Number(row.mrp) || 0
        const salePrice = Number(row.sale_price) || 0
        const wholesalePrice = Number(row.wholesale_price) || 0
        const discount = mrp > 0 ? Math.round(((mrp - salePrice) / mrp) * 100) : 0

        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <LocalOffer sx={{ fontSize: 14, color: 'success.main' }} />
              <Typography variant="body2" fontWeight={600} color="success.main">
                ₹{salePrice.toLocaleString('en-IN')}
              </Typography>
              {discount > 0 && (
                <Chip
                  label={`${discount}% OFF`}
                  size="small"
                  color="error"
                  sx={{ fontSize: '0.6rem', height: 16 }}
                />
              )}
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              MRP: ₹{mrp.toLocaleString('en-IN')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Wholesale: ₹{wholesalePrice.toLocaleString('en-IN')}
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 'packaging',
      label: 'B2B Packaging Info',
      width: 160,
      render: (_, row: Product) => {
        const innerPack = row.inner_box_quantity || 0
        const cartonSize = row.carton_quantity || 0
        const moq = row.moq || 1
        const isSetSelling = row.is_set_selling

        return (
          <Box>
            {isSetSelling && (
              <Chip 
                label={`SET: ${row.set_size} pcs`} 
                size="small" 
                color="success" 
                variant="outlined"
                sx={{ mb: 1, height: 20, fontSize: '0.65rem', fontWeight: 700 }} 
              />
            )}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Package sx={{ fontSize: 14, color: 'info.main' }} />
              <Typography variant="body2" fontWeight={500}>
                Inner: {innerPack} {isSetSelling ? 'Sets' : 'pcs'}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              Carton: {cartonSize} {isSetSelling ? 'Sets' : 'pcs'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              MOQ: {moq} {isSetSelling ? 'Sets' : 'pcs'}
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 'physical',
      label: 'Physical Specs',
      width: 140,
      render: (_, row: Product) => {
        const weight = row.weight || 0
        const dimensions = row.dimensions || {}

        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Scale sx={{ fontSize: 14, color: 'warning.main' }} />
              <Typography variant="body2" fontWeight={500}>
                {weight} kg
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Straighten sx={{ fontSize: 12, color: 'text.secondary' }} />
              <Typography variant="caption" color="text.secondary">
                {dimensions.length}×{dimensions.width}×{dimensions.height} cm
              </Typography>
            </Box>
          </Box>
        )
      },
    },
    {
      id: 'inventory',
      label: 'Stock',
      width: 100,
      render: (_, row: Product) => {
        const totalStock = row.total_stock || 0

        return (
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <Inventory sx={{
                fontSize: 14,
                color: 'primary.main'
              }} />
              <Typography
                variant="body2"
                fontWeight={600}
              >
                {totalStock}
              </Typography>
            </Box>
            <Typography variant="caption" color="text.secondary">
              All warehouses
            </Typography>
          </Box>
        )
      },
    },
    {
      id: 'status',
      label: 'Status',
      width: 120,
      render: (_, row: Product) => {
        const config = getStatusConfig(row.status)

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {config.icon}
            <Chip
              label={config.label}
              color={config.color as any}
              size="small"
              sx={{ minWidth: 70 }}
            />
          </Box>
        )
      },
    },
  ]

  // Drop lower-priority columns on narrower viewports so the table doesn't
  // rely purely on horizontal scrolling to be usable.
  const visibleColumns = columns.filter((column) => {
    if (isBelowMd) {
      return ['product', 'pricing', 'status'].includes(column.id)
    }
    if (isBelowLg) {
      return ['product', 'category', 'pricing', 'packaging', 'status'].includes(column.id)
    }
    return true
  })

  const actions: TableAction[] = [
    {
      icon: <VisibilityIcon />,
      label: 'View',
      onClick: handleView,
      color: 'primary',
    },
    {
      icon: <ShoppingCart />,
      label: 'View on Store',
      onClick: (row: Product) => {
        const baseUrl = (import.meta as any).env.VITE_CUSTOMER_PORTAL_URL
        if (baseUrl) {
          window.open(`${baseUrl}/products/${row.id}`, '_blank')
        } else {
          enqueueSnackbar('Front store URL not configured', { variant: 'error' })
        }
      },
      color: 'success',
    },
    ...(hasPermission('products.update') ? [{
      icon: <EditIcon />,
      label: 'Edit',
      onClick: handleEdit,
      color: 'secondary' as const,
    }] : []),
    ...(hasPermission('products.delete') ? [{
      icon: <DeleteIcon />,
      label: 'Delete',
      onClick: (row: Product) => handleDelete(row.id),
      color: 'error' as const,
    }] : []),
  ]

  // Fetch analytics data separately (all products, not paginated)
  const { data: analyticsData } = useGetProductsQuery({
    per_page: 100, // Max allowed by backend
  })

  // Calculate stats from all products
  const stats = React.useMemo(() => {
    const allProducts = analyticsData?.items || []
    return {
      total: analyticsData?.total || 0, // Use total from API
      active: allProducts.filter(p => p.status === 'ACTIVE').length,
      lowStock: 0, // Stock alerts are now managed separately at warehouse level
      draft: allProducts.filter(p => p.status === 'DRAFT').length,
    }
  }, [analyticsData])

  return (
    <Box sx={{ p: 3, bgcolor: '#FAFAFA', minHeight: '100vh' }}>
      {/* Header with Brand Gradient */}
      <Paper
        sx={{
          background: 'linear-gradient(135deg, #2196f3 0%, #0d47a1 100%)',
          color: 'white',
          p: 3,
          borderRadius: 3,
          mb: 3,
          boxShadow: '0 4px 16px rgba(0,0,0,0.15)'
        }}
      >
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={600} gutterBottom>
              B2B Product Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Comprehensive product catalog management with B2B features
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {hasPermission('products.bulk_import') && (
              <Button
                variant="outlined"
                startIcon={<UploadIcon />}
                onClick={() => setImportDialogOpen(true)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Import Products
              </Button>
            )}
            {hasPermission('products.bulk_export') && (
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={() => setExportDialogOpen(true)}
                sx={{
                  borderColor: 'rgba(255,255,255,0.5)',
                  color: 'white',
                  '&:hover': {
                    borderColor: 'white',
                    bgcolor: 'rgba(255,255,255,0.1)'
                  }
                }}
              >
                Export Catalog
              </Button>
            )}
            {hasPermission('products.create') && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={handleCreate}
                sx={{
                  bgcolor: '#f44336',
                  '&:hover': { bgcolor: '#c62828' },
                  boxShadow: '0 2px 8px rgba(244,67,54,0.3)'
                }}
              >
                Add Product
              </Button>
            )}
          </Box>
        </Box>

        {/* Stats Cards */}
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white' }}>
                  <ShoppingCart />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.total}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Total Products
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(76,175,80,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(76,175,80,0.3)', color: 'white' }}>
                  <CheckCircle />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.active}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Active Products
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255,152,0,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,152,0,0.3)', color: 'white' }}>
                  <Warning />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.lowStock}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Low Stock Alerts
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card sx={{ bgcolor: 'rgba(255,193,7,0.2)', backdropFilter: 'blur(10px)' }}>
              <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{ bgcolor: 'rgba(255,193,7,0.3)', color: 'white' }}>
                  <EditIcon />
                </Avatar>
                <Box>
                  <Typography variant="h6" color="white" fontWeight={600}>
                    {stats.draft}
                  </Typography>
                  <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                    Draft Products
                  </Typography>
                </Box>
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      {/* Search and Filters */}
      <Card sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search by name, SKU, HSN, category, or brand..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="primary" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearchTerm('')}>
                      <Clear />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                },
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Status"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as ProductStatus | '')}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              InputProps={{
                endAdornment: statusFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setStatusFilter('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="ACTIVE">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle fontSize="small" sx={{ color: 'success.main' }} />
                  Active
                </Box>
              </MenuItem>
              <MenuItem value="DRAFT">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EditIcon fontSize="small" sx={{ color: 'warning.main' }} />
                  Draft
                </Box>
              </MenuItem>
              <MenuItem value="ARCHIVED">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Cancel fontSize="small" sx={{ color: 'error.main' }} />
                  Archived
                </Box>
              </MenuItem>
              <MenuItem value="OUT_OF_STOCK">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Warning fontSize="small" sx={{ color: 'warning.main' }} />
                  Out of Stock
                </Box>
              </MenuItem>
              <MenuItem value="DISCONTINUED">
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Cancel fontSize="small" sx={{ color: 'text.secondary' }} />
                  Discontinued
                </Box>
              </MenuItem>
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              select
              fullWidth
              label="Category"
              value={categoryFilter}
              onChange={(e) => setCategoryFilter(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              disabled={isLoadingCategories}
              InputProps={{
                endAdornment: categoryFilter && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setCategoryFilter('')}>
                      <Clear fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            >
              <MenuItem value="">All Categories</MenuItem>
              {categoriesData?.items.map((category) => (
                <MenuItem key={category.id} value={category.id}>
                  {category.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Tooltip title="Clear all filters">
              <span style={{ width: '100%' }}>
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    setSearchTerm('')
                    setStatusFilter('')
                    setCategoryFilter('')
                    setPriceMin('')
                    setPriceMax('')
                    setStockMin('')
                    setStockMax('')
                    setPage(1)
                  }}
                  disabled={!searchTerm && !statusFilter && !categoryFilter && !priceMin && !priceMax && !stockMin && !stockMax}
                  sx={{
                    borderRadius: 2,
                    height: '56px',
                    minWidth: 'auto'
                  }}
                >
                  <FilterListIcon />
                </Button>
              </span>
            </Tooltip>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_, newMode) => newMode && setViewMode(newMode)}
                size="small"
                aria-label="View mode"
              >
                <ToggleButton value="table" aria-label="table view">
                  <Tooltip title="Table View">
                    <ViewList fontSize="small" />
                  </Tooltip>
                </ToggleButton>
                <ToggleButton value="card" aria-label="card view">
                  <Tooltip title="Card View">
                    <ViewModule fontSize="small" />
                  </Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
              <Tooltip title="Refresh products">
                <span>
                  <IconButton
                    onClick={() => refetch()}
                    disabled={isFetching}
                    sx={{
                      bgcolor: 'primary.50',
                      '&:hover': { bgcolor: 'primary.100' }
                    }}
                  >
                    <Refresh />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Price / Stock Range Filters */}
        <Grid container spacing={2} alignItems="center" sx={{ mt: 0.5 }}>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Price Min"
              type="number"
              value={priceMin}
              onChange={(e) => setPriceMin(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Price Max"
              type="number"
              value={priceMax}
              onChange={(e) => setPriceMax(e.target.value)}
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Stock Min"
              type="number"
              value={stockMin}
              onChange={(e) => setStockMin(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Stock Max"
              type="number"
              value={stockMax}
              onChange={(e) => setStockMax(e.target.value)}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />
          </Grid>
        </Grid>

        {/* Bulk Action Bar - Separate Row */}
        {selectedRows.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <BulkActionBar
              selectedItems={selectedRows}
              totalItems={data?.total || 0}
              onSelectAll={(selected) => {
                if (selected) {
                  // Select all items on current page
                  setSelectedRows(filteredProducts.map(p => p.id))
                } else {
                  setSelectedRows([])
                }
              }}
              onClearSelection={() => setSelectedRows([])}
              actions={bulkActions}
              itemName="products"
            />
          </Box>
        )}

        {/* Active Filters Display */}
        {(searchTerm || statusFilter || categoryFilter || priceMin || priceMax || stockMin || stockMax) && (
          <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" color="text.secondary">
              Active Filters:
            </Typography>
            {searchTerm && (
              <Chip
                label={`Search: "${searchTerm}"`}
                size="small"
                onDelete={() => setSearchTerm('')}
                color="primary"
                variant="outlined"
              />
            )}
            {statusFilter && (
              <Chip
                label={`Status: ${statusFilter}`}
                size="small"
                onDelete={() => setStatusFilter('')}
                color="primary"
                variant="outlined"
              />
            )}
            {categoryFilter && (
              <Chip
                label={`Category: ${categoriesData?.items.find(c => c.id === categoryFilter)?.name || 'Unknown'}`}
                size="small"
                onDelete={() => setCategoryFilter('')}
                color="primary"
                variant="outlined"
              />
            )}
            {(priceMin || priceMax) && (
              <Chip
                label={`Price: ₹${priceMin || 0} - ${priceMax ? `₹${priceMax}` : 'Any'}`}
                size="small"
                onDelete={() => { setPriceMin(''); setPriceMax('') }}
                color="primary"
                variant="outlined"
              />
            )}
            {(stockMin || stockMax) && (
              <Chip
                label={`Stock: ${stockMin || 0} - ${stockMax || 'Any'}`}
                size="small"
                onDelete={() => { setStockMin(''); setStockMax('') }}
                color="primary"
                variant="outlined"
              />
            )}
          </Box>
        )}
      </Card>

      {/* Loading State */}
      {(isLoading || isFetching) && (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Products Table/Card View */}
      {!isLoading && !isFetching && viewMode === 'table' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <DataTable
            columns={visibleColumns}
            rows={filteredProducts}
            actions={actions}
            page={page - 1}
            rowsPerPage={pageSize}
            totalRows={data?.total || 0}
            onPageChange={(newPage) => setPage(newPage + 1)}
            onRowsPerPageChange={setPageSize}
            isLoading={isLoading || isFetching}
            emptyMessage="No products found"
            enableSelection={true}
            selectedRows={selectedRows}
            onSelectRow={(id) => {
              if (selectedRows.includes(id)) {
                setSelectedRows(prev => prev.filter(rowId => rowId !== id))
              } else {
                setSelectedRows(prev => [...prev, id])
              }
            }}
            onSelectAllRows={(selected) => {
              if (selected) {
                setSelectedRows(filteredProducts.map(p => p.id))
              } else {
                setSelectedRows([])
              }
            }}
          />
        </Card>
      )}

      {!isLoading && !isFetching && viewMode === 'card' && (
        <Card sx={{ borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', p: 2 }}>
          {filteredProducts.length === 0 ? (
            <Box sx={{ py: 8, textAlign: 'center' }}>
              <Typography color="text.secondary">No products found</Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {filteredProducts.map((product) => {
                const imageUrl = getAbsoluteImageUrl(product.image) || (product.images && product.images.length > 0 ? getAbsoluteImageUrl(product.images[0]) : null)
                const statusConfig = getStatusConfig(product.status)
                const salePrice = Number(product.sale_price) || 0
                const wholesalePrice = Number(product.wholesale_price) || 0
                const isSelected = selectedRows.includes(product.id)

                return (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
                    <Card
                      variant="outlined"
                      sx={{
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                      }}
                    >
                      <Box sx={{ position: 'relative' }}>
                        <Checkbox
                          checked={isSelected}
                          onChange={() => {
                            setSelectedRows(prev =>
                              prev.includes(product.id) ? prev.filter(id => id !== product.id) : [...prev, product.id]
                            )
                          }}
                          sx={{ position: 'absolute', top: 4, left: 4, bgcolor: 'background.paper', borderRadius: '50%' }}
                          size="small"
                        />
                        {imageUrl ? (
                          <Box
                            component="img"
                            src={imageUrl}
                            alt={product.name}
                            sx={{ width: '100%', height: 160, objectFit: 'cover', bgcolor: 'grey.100' }}
                          />
                        ) : (
                          <Box sx={{ width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: 'grey.100' }}>
                            <Avatar variant="rounded" sx={{ width: 56, height: 56, fontSize: '1.5rem' }}>
                              {product.name.charAt(0).toUpperCase()}
                            </Avatar>
                          </Box>
                        )}
                      </Box>
                      <CardContent sx={{ flex: 1, pb: 1 }}>
                        <Typography variant="body2" fontWeight={600} noWrap title={product.name}>
                          {product.name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontFamily: 'monospace', mb: 0.5 }}>
                          {product.sku || product.item_code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                          {product.category?.name || 'Uncategorized'}
                        </Typography>
                        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" fontWeight={600} color="success.main">
                            ₹{salePrice.toLocaleString('en-IN')}
                          </Typography>
                          {wholesalePrice > 0 && (
                            <Typography variant="caption" color="text.secondary">
                              WS: ₹{wholesalePrice.toLocaleString('en-IN')}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1 }}>
                          <Chip
                            label={statusConfig.label}
                            size="small"
                            icon={statusConfig.icon || undefined}
                            color={statusConfig.color as any}
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                          <Chip
                            label={`Stock: ${product.total_stock || 0}`}
                            size="small"
                            variant="outlined"
                            sx={{ height: 20, fontSize: '0.65rem' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          MOQ: {product.moq || 1}{product.is_set_selling ? ` sets (${product.set_size}pcs)` : ' pcs'} · Inner: {product.inner_box_quantity || 0} · Carton: {product.carton_quantity || 0}
                        </Typography>
                      </CardContent>
                      <CardActions sx={{ justifyContent: 'flex-end', pt: 0 }}>
                        <Tooltip title="View">
                          <IconButton size="small" color="primary" onClick={() => handleView(product)}>
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {hasPermission('products.update') && (
                          <Tooltip title="Edit">
                            <IconButton size="small" color="secondary" onClick={() => handleEdit(product)}>
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {hasPermission('products.delete') && (
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error" onClick={() => handleDelete(product.id)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </CardActions>
                    </Card>
                  </Grid>
                )
              })}
            </Grid>
          )}
          <TablePagination
            component="div"
            count={data?.total || 0}
            page={page - 1}
            onPageChange={(_, newPage) => setPage(newPage + 1)}
            rowsPerPage={pageSize}
            onRowsPerPageChange={(e) => setPageSize(parseInt(e.target.value, 10))}
            rowsPerPageOptions={[10, 25, 50, 100]}
            sx={{ borderTop: 1, borderColor: 'divider', mt: 2 }}
          />
        </Card>
      )}

      {/* Progress Manager */}
      <ProgressManager
        operations={operations}
        onClearCompleted={clearCompletedOperations}
        onClearAll={clearAllOperations}
      />

      {/* Import/Export Dialogs */}
      <ProductImportDialog
        open={importDialogOpen}
        onClose={() => setImportDialogOpen(false)}
        onSuccess={() => {
          refetch()
          setImportDialogOpen(false)
        }}
      />
      <ProductExportDialog
        open={exportDialogOpen}
        onClose={() => setExportDialogOpen(false)}
      />
    </Box>
  )
}

export default Products